import {
  LayoutDashboard,
  Radar,
  Globe,
  BookOpen,
  Search,
  Building2,
  Briefcase,
  Columns3,
  Scale,
  Calculator,
  Settings,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
}

export interface NavGroup {
  id: "standalone" | "discover" | "workfiles" | "account";
  label: string | null;
  items: NavItem[];
}

// Plan B feature flags. Sectors / Regulatory / Calculator / Market Overview /
// Scanner / Defendants are paused while their data models are rebuilt;
// Discovery and Cases are the demo narrative. Flip a flag in `.env.local` to
// work on a paused module locally — production stays off until each module
// ships its replacement.
function flagEnabled(flag: string | undefined): boolean {
  return flag === "true" || flag === "1";
}
export const FEATURE_FLAGS = {
  sectors: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_SECTORS),
  regulatory: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_REGULATORY),
  calculator: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_CALCULATOR),
  marketOverview: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_MARKET_OVERVIEW),
  scanner: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_SCANNER),
  defendants: flagEnabled(process.env.NEXT_PUBLIC_ENABLE_DEFENDANTS),
};

const ALL_NAV_GROUPS: NavGroup[] = [
  {
    id: "standalone",
    label: null,
    items: [{ href: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    id: "discover",
    label: "Discover",
    items: [
      { href: "/discovery", label: "Discovery", icon: Radar },
      ...(FEATURE_FLAGS.sectors
        ? [{ href: "/sectors", label: "Sectors", icon: Globe }]
        : []),
      ...(FEATURE_FLAGS.regulatory
        ? [{ href: "/regulatory", label: "Regulatory", icon: BookOpen }]
        : []),
      ...(FEATURE_FLAGS.scanner
        ? [{ href: "/scanner", label: "Scanner", icon: Search }]
        : []),
      ...(FEATURE_FLAGS.defendants
        ? [{ href: "/defendants", label: "Defendants", icon: Building2 }]
        : []),
    ],
  },
  {
    id: "workfiles",
    label: "Workfiles",
    items: [
      { href: "/cases", label: "Cases", icon: Briefcase },
      { href: "/pipeline", label: "Pipeline", icon: Columns3 },
      { href: "/analysis", label: "Analysis", icon: Scale },
      ...(FEATURE_FLAGS.calculator
        ? [{ href: "/calculator", label: "Calculator", icon: Calculator }]
        : []),
    ],
  },
  {
    id: "account",
    label: "Account",
    items: [
      // Goes to /settings, which redirects to /settings/team (only
      // section today). The settings shell handles sub-section nav.
      { href: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

export const NAV_GROUPS: NavGroup[] = ALL_NAV_GROUPS;

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

export function isActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(href + "/");
}
